import { createContext, useContext, type ReactNode } from 'react';

/**
 * Modal stack context — automatically stacks nested modals above their parents
 * without requiring hand-tuned z-index values.
 *
 * Usage:
 *   1. Each modal calls `useModalZIndex(defaultZIndex)` to get its effective z-index.
 *      - If nothing above it provides this context, the modal uses its `defaultZIndex` (current behavior).
 *      - If a parent modal provides this context, the modal's z-index becomes `parentZ + increment`
 *        (so it stacks visually above the parent).
 *
 *   2. Each modal wraps the JSX that may contain *other* modals in <ModalStackProvider zIndex={myZ}>.
 *      Nested modals will then resolve their z-index from this context.
 *
 * No hardcoded z-index values need to be changed for the default (non-nested) case,
 * so adding/removing nesting in one place doesn't risk breaking unrelated screens.
 */

type ModalStackValue = { baseZ: number };

const ModalStackContext = createContext<ModalStackValue | null>(null);

export function ModalStackProvider({
  children,
  zIndex,
}: {
  children: ReactNode;
  zIndex: number;
}) {
  return (
    <ModalStackContext.Provider value={{ baseZ: zIndex }}>
      {children}
    </ModalStackContext.Provider>
  );
}

/**
 * Returns the effective z-index for the current modal.
 *
 * @param defaultZ The z-index to use when this modal is *not* nested inside another modal.
 * @param increment How much to add above the parent modal's z-index when nested. Defaults to 10.
 */
export function useModalZIndex(defaultZ: number, increment = 10): number {
  const ctx = useContext(ModalStackContext);
  if (!ctx) return defaultZ;
  // Always stack above the parent; if the parent's z-index is somehow lower than our default,
  // use whichever is greater so default-styled modals never appear behind their parent.
  return Math.max(defaultZ, ctx.baseZ + increment);
}
